import React, {useEffect, useState} from 'react';
import MomentUtils from '@date-io/moment';
import {Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import ButtonLoader from '@components/system/ButtonLoader/ButtonLoader';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  TextField,
  TextareaAutosize
} from '@material-ui/core';
import moment from 'moment';
import {calculateDurationBetweenStartAndStopTime} from '../../../ChronicCareManagement/module-components/StopTimerDialog/TimerUtils';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import {useSnackbar} from 'notistack';
import {useStyles} from './AddWorkLogTimeStyles';
import {addWorkLogTime} from '@shared/services/WorkflowServices';
import {MuiPickersUtilsProvider} from '@material-ui/pickers';
import {TICKETING_SYSTEM_MODULE_NAME_TO_MODULE_ID_MAPPER} from '@shared/constants/Mapping';
import {IAllPossibleTicketModules} from '../../hooks/useTicketingSystemPermission';

interface AddWorkLogTimeDialogProps {
  open: boolean;
  onClose: () => void;
  clinicGroupId: number;
  clinicId: number;
  patientTicketId: number;
  module: IAllPossibleTicketModules;
  handleWorkLogAction(): void;
}
type StateProps = {
  notes: string;
  buttonLoader: boolean;
};
const AddWorkLogTimeDialog = (
  props: AddWorkLogTimeDialogProps
): React.ReactElement => {
  const {
    open,
    onClose,
    clinicGroupId,
    clinicId,
    patientTicketId,
    module,
    handleWorkLogAction
  } = props;
  const classes = useStyles();
  const {enqueueSnackbar} = useSnackbar();
  const {t} = useTranslation();
  const [selectedTime, setSelectedTime] = useState<{
    selectedStartTime: null | any;
    selectedStopTime: null | any;
  }>({
    selectedStartTime: null,
    selectedStopTime: null
  });
  const [stateValues, setState] = useState<StateProps>({
    notes: '',
    buttonLoader: false
  });
  const {buttonLoader, notes} = stateValues;
  const {selectedStartTime, selectedStopTime} = selectedTime;
  const {control, errors, handleSubmit, setValue, trigger} = useForm({
    mode: 'all',
    resolver: async (values: any) => {
      const error: any = {};
      const currentMoment = moment(); // To ensure the same reference time for all checks
      // Validation for start_time
      if (!values.start_time) {
        error.start_time = t('PRIOR_AUTHORIZATION.ADD_WORK_LOG.START_TIME_IS_REQUIRED');
      } else {
        const startTimeMoment = moment(values.start_time);
        if (!startTimeMoment.isValid()) {
          error.start_time = t('PRIOR_AUTHORIZATION.ADD_WORK_LOG.INVALID_TIME_FORMAT');
        } else if (startTimeMoment.isAfter(currentMoment)) {
          error.start_time = t('PRIOR_AUTHORIZATION.ADD_WORK_LOG.NO_FUTURE_TIME');
        }
      }
      // Validation for stop_time
      if (!values.stop_time) {
        error.stop_time = t('PRIOR_AUTHORIZATION.ADD_WORK_LOG.STOP_TIME_IS_REQUIRED');
      } else {
        const stopTimeMoment = moment(values.stop_time);
        if (!stopTimeMoment.isValid()) {
          error.stop_time = t('PRIOR_AUTHORIZATION.ADD_WORK_LOG.INVALID_TIME_FORMAT');
        } else if (stopTimeMoment.isAfter(currentMoment)) {
          error.stop_time = t('PRIOR_AUTHORIZATION.ADD_WORK_LOG.NO_FUTURE_TIME');
        } else if (values.start_time && stopTimeMoment.isBefore(moment(values.start_time))) {
          error.stop_time = t('PRIOR_AUTHORIZATION.ADD_WORK_LOG.INVALID_TIME_RANGE');
        }
      }
      // Additional check to compare start_time and stop_time
      if (values.start_time && values.stop_time) {
        const startTime = moment(values.start_time);
        const stopTime = moment(values.stop_time);
        if (startTime.isValid() && stopTime.isValid() && stopTime.diff(startTime, 'seconds') === 0) {
          error.stop_time = t('PRIOR_AUTHORIZATION.ADD_WORK_LOG.FIRST_TIME_AND_END_TIME_MUST_BE_DIFFERENT');
        }
      }
      return {
        values,
        errors: error
      };
    }
  });

  const handleStartTimeChange = (time: any) => {
    // Remove the seconds, because the library preserve the seconds.
    let formattedTime = time;
    if (formattedTime?.isValid?.()) {
      formattedTime = time.seconds(0);
    }
    setSelectedTime((prevState: any) => ({
      ...prevState,
      selectedStartTime: formattedTime ?? null
    }));
    setValue('start_time', formattedTime, {
      shouldValidate: true,
      shouldDirty: true
    });
  };

  const handleStopTimeChange = (time: any) => {
    // Remove the seconds, because the library preserve the seconds.
    let formattedTime = time;
    if (formattedTime?.isValid?.()) {
      formattedTime = time.seconds(0);
    }
    setSelectedTime((prevState: any) => ({
      ...prevState,
      selectedStopTime: formattedTime ?? null
    }));
    setValue('stop_time', formattedTime, {
      shouldValidate: true,
      shouldDirty: true
    });
  };
  const handleNotesChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    event.persist();
    setState(prevState => ({
      ...prevState,
      notes: event.target.value
    }));
  };

  const renderOptionalNotesTextArea = () => {
    return (
      <div>
        <TextareaAutosize
          rowsMax={10}
          rows={4}
          aria-label="maximum height"
          placeholder={t('PRIOR_AUTHORIZATION.ADD_WORK_LOG.ENTER_SOME_NOTES')}
          className={`app-text-area ${classes.TextareaAutosize}`}
          onChange={handleNotesChange}
          value={notes}
        />
      </div>
    );
  };

  // ** calculate the duration here */
  const formattedDuration = calculateDurationBetweenStartAndStopTime(
    selectedStartTime,
    selectedStopTime,
    true
  );

  const renderDialogContent = () => {
    return (
      <div>
        <Grid container>
          <Grid container direction="row" spacing={2}>
            <Grid item xs={12} md={6}>
              <div className={classes.marginBottom}>
                {t('PRIOR_AUTHORIZATION.ADD_WORK_LOG.START_TIME')}
              </div>
              <FormControl
                fullWidth={true}
                error={!!errors?.start_time}
                className={classes.formControl}>
                <Controller
                  control={control}
                  name="start_time"
                  error={!!errors?.start_time}
                  render={({onBlur}) => (
                    <MuiPickersUtilsProvider
                      utils={MomentUtils}
                      libInstance={moment}>
                      <TextField
                        variant="outlined"
                        className={classes.datePickerValue}
                        id="datetime-local"
                        type="datetime-local"
                        InputLabelProps={{
                          shrink: true
                        }}
                        inputProps={{
                          max: moment().format('YYYY-MM-DDTHH:mm') // Set the max attribute with the current date and time
                        }}
                        onChange={(event: any) => {
                          handleStartTimeChange(event.target.value);
                        }}
                        error={!!errors?.start_time}
                      />
                    </MuiPickersUtilsProvider>
                  )}
                />
                {errors?.start_time && (
                  <FormHelperText>{errors.start_time}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item className={classes.gridPadding} xs={12} md={6}>
              <div>
                <div className={classes.marginBottom}>
                  {t('PRIOR_AUTHORIZATION.ADD_WORK_LOG.END_TIME')}
                </div>
                <div className={classes.stopTimeInputWrapper}>
                  <FormControl
                    fullWidth={true}
                    error={!!errors?.stop_time}
                    className={classes.formControl}>
                    <Controller
                      control={control}
                      name="stop_time"
                      error={!!errors?.stop_time}
                      render={({onBlur}) => (
                        <MuiPickersUtilsProvider
                          utils={MomentUtils}
                          libInstance={moment}>
                          <TextField
                            variant="outlined"
                            className={classes.datePickerValue}
                            id="datetime-local"
                            type="datetime-local"
                            InputLabelProps={{
                              shrink: true
                            }}
                            inputProps={{
                              max: moment().format('YYYY-MM-DDTHH:mm') // Set the max attribute with the current date and time
                            }}
                            onChange={(event: any) => {
                              handleStopTimeChange(event.target.value);
                            }}
                            error={!!errors?.stop_time}
                          />
                        </MuiPickersUtilsProvider>
                      )}
                    />
                    {errors?.stop_time && (
                      <FormHelperText>{errors.stop_time}</FormHelperText>
                    )}
                  </FormControl>
                </div>
              </div>
            </Grid>
            <Grid item xs={12} md={6}>
              <div className={classes.marginBottom}>
                {t('PRIOR_AUTHORIZATION.ADD_WORK_LOG.DURATION')}
              </div>
              <div>{formattedDuration ? formattedDuration : '-'}</div>
            </Grid>
          </Grid>
        </Grid>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <div className={classes.notesWrapper}>
              {t('PRIOR_AUTHORIZATION.ADD_WORK_LOG.NOTES')}
            </div>
            {renderOptionalNotesTextArea()}
          </Grid>
        </Grid>
      </div>
    );
  };

  const handleSaveClick = async (data: any): Promise<void> => {
    // const duration = calculateDurationBetweenStartAndStopTime(
    //   selectedStartTime,
    //   selectedStopTime
    // );
    const utcStartDatetime = moment(selectedStartTime).utc().format();
    const utcStopDatetime = moment(selectedStopTime).utc().format();
    try {
      setState(prevState => ({
        ...prevState,
        buttonLoader: true
      }));
      let trimmedNotes = null;
      if (notes && typeof notes === 'string' && notes.trim()) {
        trimmedNotes = notes.trim();
      }
      const payload: any = {
        start_time: utcStartDatetime,
        stop_time: utcStopDatetime,
        // time_spent: convertTimeToSeconds(duration),
        notes: trimmedNotes
      };
      const res = await addWorkLogTime(
        clinicGroupId,
        clinicId,
        patientTicketId,
        payload,
        {
          module: TICKETING_SYSTEM_MODULE_NAME_TO_MODULE_ID_MAPPER[module]
        }
      );
      if (res) {
        setState(prevState => ({
          ...prevState,
          buttonLoader: false
        }));
        enqueueSnackbar(
          t('PRIOR_AUTHORIZATION.ADD_WORK_LOG.ADDED_WORK_LOG'),
          SnackBarConfig('s')
        );
        handleWorkLogAction();
      }
      onClose();
    } catch (error: any) {
      setState(prevState => ({
        ...prevState,
        buttonLoader: false
      }));
      enqueueSnackbar(error && error?.message, SnackBarConfig('e'));
    } finally {
      setState(prevState => ({
        ...prevState,
        buttonLoader: false
      }));
    }
  };

  const renderButtons = () => {
    return (
      <div>
        <Button onClick={onClose}>{t('COMMON.CANCEL')}</Button>
        <Button
          disabled={buttonLoader}
          type="submit"
          variant={'contained'}
          onClick={handleSubmit(handleSaveClick)}
          color="primary">
          {buttonLoader ? <ButtonLoader color={'white'} /> : t('COMMON.SAVE')}
        </Button>
      </div>
    );
  };

  // When ever start time changes validate the end time too
  useEffect(() => {
    if (selectedTime.selectedStartTime || selectedTime.selectedStopTime) {
      trigger('stop_time');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTime.selectedStartTime]);

  return (
    <Dialog
      maxWidth={'sm'}
      fullWidth={true}
      disableBackdropClick={true}
      disableEscapeKeyDown={true}
      open={open}
      onClose={onClose}
      aria-labelledby="enrollment-dialog-title">
      <DialogTitle disableTypography id="enrollment-dialog-title">
        {t('PRIOR_AUTHORIZATION.ADD_WORK_LOG.TITLE')}
      </DialogTitle>
      <DialogContent dividers>{renderDialogContent()}</DialogContent>
      <DialogActions className={classes.addWorkLogTimeDialogActions}>
        {renderButtons()}
      </DialogActions>
    </Dialog>
  );
};
export default AddWorkLogTimeDialog;
